// widget/src/components/VoiceOrb.tsx
//
// Renders BOTH the live oscilloscope `.scope` element and the idle
// `.orb` button (plus its 5 pulsing bars). The CSS handles the
// swap — `.widget-host.recording` hides the orb and shows the scope.
//
// Press-vs-hold detection lives here: a quick tap fires `onTap` (which
// the parent uses to open the chat); a hold of >250ms starts recording,
// and on release the captured blob is sent via `onCapture`.

import { useCallback, useEffect, useRef } from "react"
import type { useRecorder } from "../hooks/useRecorder"
import styles from "../styles/widget.module.css"

const HOLD_THRESHOLD_MS = 250

interface Props {
  recorder: ReturnType<typeof useRecorder>
  onTap: () => void
  onCapture: (blob: Blob | null) => void
}

export function VoiceOrb({ recorder, onTap, onCapture }: Props) {
  const { isRecording, analyser, start, stop } = recorder
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number | null>(null)
  const holdTimerRef = useRef<number | null>(null)
  const didHoldRef = useRef(false)

  // ── Oscilloscope draw loop ─────────────────────────────────────────
  useEffect(() => {
    if (!isRecording) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      const canvas = canvasRef.current
      const ctx = canvas?.getContext("2d")
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      return
    }
    const canvas = canvasRef.current
    const an = analyser.current
    if (!canvas || !an) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const buffer = new Uint8Array(an.fftSize)

    function draw() {
      if (!analyser.current) return
      analyser.current.getByteTimeDomainData(buffer)
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height)
      ctx!.lineWidth = 3
      ctx!.strokeStyle = "rgba(255,255,255,0.95)"
      ctx!.beginPath()
      const slice = canvas!.width / buffer.length
      let x = 0
      for (let i = 0; i < buffer.length; i++) {
        const v = buffer[i] / 128.0
        const y = (v * canvas!.height) / 2
        if (i === 0) ctx!.moveTo(x, y)
        else ctx!.lineTo(x, y)
        x += slice
      }
      ctx!.stroke()
      rafRef.current = requestAnimationFrame(draw)
    }
    draw()

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [isRecording, analyser])

  // ── Press-vs-hold detection ────────────────────────────────────────
  const onPressStart = useCallback(
    (e: React.SyntheticEvent) => {
      if (e.cancelable) e.preventDefault()
      didHoldRef.current = false
      holdTimerRef.current = window.setTimeout(() => {
        didHoldRef.current = true
        void start()
      }, HOLD_THRESHOLD_MS)
    },
    [start],
  )

  const onPressEnd = useCallback(async () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
    if (didHoldRef.current) {
      const blob = await stop()
      onCapture(blob)
    } else {
      onTap()
    }
  }, [onCapture, onTap, stop])

  // Stop recording if pointer leaves the orb without a release event
  // (e.g. mouse wandered off-screen).
  useEffect(() => {
    function onUp() {
      if (holdTimerRef.current || didHoldRef.current) {
        void onPressEnd()
      }
    }
    window.addEventListener("mouseup", onUp)
    window.addEventListener("touchend", onUp)
    window.addEventListener("touchcancel", onUp)
    return () => {
      window.removeEventListener("mouseup", onUp)
      window.removeEventListener("touchend", onUp)
      window.removeEventListener("touchcancel", onUp)
    }
  }, [onPressEnd])

  return (
    <>
      <div className={styles.scope}>
        <canvas ref={canvasRef} width={160} height={160} />
      </div>

      <button
        type="button"
        className={styles.orb}
        onMouseDown={onPressStart}
        onTouchStart={onPressStart}
        aria-label="Open chat — hold to record voice"
      >
        <span className={styles["rec-hint"]}>Recording — release to send</span>
        <span className={styles.bar} />
        <span className={styles.bar} />
        <span className={styles.bar} />
        <span className={styles.bar} />
        <span className={styles.bar} />
      </button>
    </>
  )
}
